import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import '../../styles/dash/WeeklyExerciseView.css';
import { tryOpenAppThenStore, APP_STORE_URL, PLAY_STORE_URL } from '../../utils/appStoreLinks';

const UUID_REGEX = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export default function PublicPlaylistView({ identifierOverride }) {
    const { identifier: routeIdentifier } = useParams();
    const identifier = identifierOverride || routeIdentifier;
    const [playlist, setPlaylist] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [shareMessage, setShareMessage] = useState('');

    useEffect(() => {
        fetchPlaylist();
    }, [identifier]);

    const fetchPlaylist = async () => {
        if (!identifier) {
            setError('Ugyldig spilleliste-lenke');
            setLoading(false);
            return;
        }

        const baseUrl = process.env.REACT_APP_BACKEND_URL;

        try {
            const response = await axios.get(
                `${baseUrl}/api/playlists/public/${identifier}`
            );
            setPlaylist(response.data);
        } catch (publicErr) {
            console.warn('Public playlist fetch failed, trying authenticated fallback:', publicErr);

            try {
                const response = await axios.get(
                    `${baseUrl}/api/playlists/${identifier}`,
                    { withCredentials: true }
                );
                if (response.data?.is_public) {
                    setPlaylist(response.data);
                } else {
                    setError('Kunne ikke hente spilleliste. Den er kanskje ikke offentlig.');
                }
            } catch (err) {
                setError('Kunne ikke hente spilleliste. Den er kanskje ikke offentlig.');
                console.error('Error:', err);
            }
        } finally {
            setLoading(false);
        }
    };

    const shareUuid = playlist?.uuid || (UUID_REGEX.test(identifier || '') ? identifier : null);
    const shareId = playlist?.idplaylist?.toString() || (/^\d+$/.test(identifier || '') ? identifier : null);
    const shareSlug = shareUuid || shareId;
    const shareUrl = shareSlug
        ? `https://lopeglad.no/playlists/${shareUuid || shareId}`
        : `https://lopeglad.no/playlists/${identifier}`;

    const handleShare = async () => {
        const playlistName = playlist?.name || 'Spilleliste';
        const message = `Sjekk ut spillelisten "${playlistName}" på Lopeglad!\n\n${shareUrl}`;

        try {
            if (navigator.share) {
                await navigator.share({
                    title: playlistName,
                    text: message,
                    url: shareUrl,
                });
            } else {
                await navigator.clipboard.writeText(message);
                setShareMessage('Kopiert!');
                setTimeout(() => setShareMessage(''), 2500);
            }
        } catch (err) {
            if (err?.name !== 'AbortError') {
                console.error('Share error:', err);
            }
        }
    };

    const handleOpenInApp = (e) => {
        e.preventDefault();
        const deeplinkId = shareUuid || shareId || identifier;
        tryOpenAppThenStore(`lopeglad://playlists/${deeplinkId}`);
    };

    if (loading) return <div className="loading">Laster...</div>;
    if (error) return <div className="error-message">{error}</div>;
    if (!playlist) return <div className="not-found">Spilleliste ikke funnet</div>;

    const activities = [...(playlist.activities || [])].sort(
        (a, b) => (a.order ?? 0) - (b.order ?? 0)
    );
    const totalMinutes = activities.reduce((sum, a) => sum + (a.minutes || 0), 0);
    const deeplinkId = shareUuid || shareId || identifier;

    return (
        <div className="weekly-exercise-view">
            <div className="exercise-hero">
                <span className="week-badge">Spilleliste</span>
                <h1 className="exercise-title">{playlist.name}</h1>
                {activities.length > 0 && (
                    <div className="exercise-meta">
                        <span className="meta-pill">{activities.length} økter</span>
                        {totalMinutes > 0 && (
                            <span className="meta-pill meta-minutes">{totalMinutes} min</span>
                        )}
                    </div>
                )}
            </div>

            <div className="exercise-content">
                {activities.length > 0 ? (
                    <div className="description">
                        <h3>Økter i spillelisten</h3>
                        <ul className="playlist-activity-list">
                            {activities.map((activity, index) => (
                                <li key={activity.id ?? activity.idactivity ?? index} className="playlist-activity-item">
                                    <span className="playlist-activity-order">{index + 1}</span>
                                    <div className="playlist-activity-info">
                                        <strong>{activity.name}</strong>
                                        {activity.short_description && (
                                            <p>{activity.short_description}</p>
                                        )}
                                    </div>
                                    {activity.minutes != null && (
                                        <span className="playlist-activity-duration">{activity.minutes} min</span>
                                    )}
                                </li>
                            ))}
                        </ul>
                    </div>
                ) : (
                    <div className="description">
                        <p>Denne spillelisten har ingen økter ennå.</p>
                    </div>
                )}
            </div>

            <div className="exercise-footer">
                <p className="cta-teaser">Klar for øktene? Åpne appen og start spillelisten.</p>
                <div className="cta-button-row">
                    <a
                        href={`lopeglad://playlists/${deeplinkId}`}
                        className="cta-app-button"
                        onClick={handleOpenInApp}
                    >
                        <span className="cta-app-button-icon">▶</span>
                        Åpne i appen
                    </a>
                    <button
                        type="button"
                        className={`cta-share-button${shareMessage ? ' copied' : ''}`}
                        onClick={handleShare}
                    >
                        {shareMessage || 'Del spilleliste'}
                    </button>
                </div>
                <p className="store-links-hint">
                    Har du ikke appen installert? Last ned på{' '}
                    <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
                    {' '}eller{' '}
                    <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>.
                </p>
            </div>
        </div>
    );
}
